// Considered: ChannelStatusCalloutMolecule alone — reused directly; extended with 'approved' variant.
// MarketplaceActivityRowMolecule reused for activity log. No new component forking needed.
// See spec Reuse Audit.

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import {
  getChannelActivity,
  submitChannelForReview,
  type GetChannelActivityResponse,
} from '../../../api/marketplaceApi';
import type { Channel } from 'types/channel';
import type { MarketplaceActivity } from 'types/marketplace-activity';
import ChannelStatusCalloutMolecule, {
  type ChannelReviewStatus,
} from '../../molecules/marketplace/ChannelStatusCalloutMolecule';
import MarketplaceActivityRowMolecule from '../../molecules/marketplace/MarketplaceActivityRowMolecule';
import './ChannelVerificationSectionOrganism.scss';

export interface ChannelVerificationSectionOrganismProps {
  channel: Channel;
  onChannelUpdated?: () => void;
}

/** Statuses that map directly to ChannelReviewStatus for the callout molecule. */
const CALLOUT_STATUSES = new Set<string>([
  'draft',
  'pending_review',
  'rejected',
  'approved',
]);

const ChannelVerificationSectionOrganism: React.FC<
  ChannelVerificationSectionOrganismProps
> = ({ channel, onChannelUpdated }) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const [activity, setActivity] = useState<MarketplaceActivity[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!apiClients) {
      setActivityLoading(false);
      return;
    }
    setActivityLoading(true);
    getChannelActivity(apiClients.schemaClient, channel.id)
      .then((res: GetChannelActivityResponse) => setActivity(res.activity))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [apiClients, channel.id]);

  const handleSubmit = useCallback(async () => {
    if (!apiClients) return;
    setSubmitting(true);
    try {
      await submitChannelForReview(apiClients.schemaClient, channel.id);
      onChannelUpdated?.();
    } finally {
      setSubmitting(false);
    }
  }, [apiClients, channel.id, onChannelUpdated]);

  const calloutStatus = CALLOUT_STATUSES.has(channel.status)
    ? (channel.status as ChannelReviewStatus)
    : 'draft';

  return (
    <div className="channel-verification">
      {/* Marketplace status card */}
      <div className="channel-verification__section-card">
        <div className="channel-verification__section-header">
          <h2 className="channel-verification__section-title">
            {t('marketplace.channelManage.v3Verification.status.title')}
          </h2>
          <p className="channel-verification__section-subtitle">
            {t('marketplace.channelManage.v3Verification.status.subtitle')}
          </p>
        </div>

        <ChannelStatusCalloutMolecule
          status={calloutStatus}
          reviewerNote={channel.rejection_reason ?? undefined}
          onResubmit={
            channel.status === 'rejected' && apiClients
              ? handleSubmit
              : undefined
          }
        />

        {channel.status === 'draft' && (
          <div className="channel-verification__submit-row">
            <button
              type="button"
              className="channel-verification__submit-btn"
              onClick={handleSubmit}
              disabled={submitting || !apiClients}
              aria-label={t('marketplace.channelManage.v3Verification.submitForReview')}
            >
              <i className="pi pi-send" aria-hidden="true" />
              {submitting
                ? t('marketplace.channelManage.v3Verification.submitting')
                : t('marketplace.channelManage.v3Verification.submitForReview')}
            </button>
          </div>
        )}
      </div>

      {/* Activity log card */}
      <div className="channel-verification__section-card">
        <div className="channel-verification__section-header">
          <h2 className="channel-verification__section-title">
            {t('marketplace.channelManage.v3Verification.activityLog.title')}
          </h2>
          <p className="channel-verification__section-subtitle">
            {t('marketplace.channelManage.v3Verification.activityLog.subtitle')}
          </p>
        </div>

        {activityLoading && (
          <div className="channel-verification__loading">
            <span className="pi pi-spin pi-spinner" aria-hidden="true" />
          </div>
        )}
        {!activityLoading && activity.length === 0 && (
          <p className="channel-verification__empty">
            {t('marketplace.channelManage.v3Verification.activityLog.empty')}
          </p>
        )}
        {!activityLoading && activity.length > 0 && (
          <div className="channel-verification__activity-list">
            {activity.map((entry, i) => (
              <MarketplaceActivityRowMolecule
                key={entry.id}
                activity={entry}
                className={
                  i === activity.length - 1
                    ? 'channel-verification__activity-row--last'
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

ChannelVerificationSectionOrganism.displayName = 'ChannelVerificationSectionOrganism';

export default ChannelVerificationSectionOrganism;
